import type { CSSProperties, KeyboardEvent } from 'react';
import { Icon, type IconName } from './Icon';
import type { SaveState } from '../game/types';

/* ---------------- Multiple-choice answer option ---------------- */
export type OptionState = 'default' | 'selected' | 'saving' | 'saved' | 'locked' | 'locked-mine' | 'correct' | 'incorrect' | 'dimmed';
const TAIL: Partial<Record<OptionState, [string, IconName]>> = {
  selected: ['Not saved', 'wifiOff'], saving: ['Saving', 'dot'], saved: ['Saved', 'check'], 'locked-mine': ['Locked in', 'lock'],
};

export interface AnswerOptionProps {
  letter: string; label: string; state: OptionState;
  /** Overrides the default trailing label (for reveal: "Correct", "Yours"). */
  tail?: { text: string; icon: IconName } | null;
  onSelect?: () => void; disabled?: boolean; size?: 'phone' | 'tv'; style?: CSSProperties; className?: string;
  /** reveal-only count bar (TV) */
  count?: { value: number; pct: number } | null;
}

/** One answer button. Interactive only while onSelect is supplied (question phase). */
export function AnswerOption({ letter, label, state, tail, onSelect, disabled = false, size = 'phone', style, className, count }: AnswerOptionProps) {
  const t = tail === undefined ? (TAIL[state] ? { text: TAIL[state]![0], icon: TAIL[state]![1] } : null) : tail;
  const tv = size === 'tv';
  const interactive = !!onSelect;
  const body = (
    <>
      <span className="ex-option__key" aria-hidden="true" style={tv ? { width: 64, height: 64, fontSize: 30 } : undefined}>{letter}</span>
      <span className="ex-option__text">{label}</span>
      {t && <span className="ex-option__tail" style={tv ? { fontSize: 18 } : undefined}><Icon name={t.icon} />{t.text}</span>}
      {count && (
        <span style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 14, paddingLeft: tv ? 86 : 48 }}>
          <span style={{ flex: 1, height: 14, borderRadius: 7, background: state === 'dimmed' ? 'rgba(185,195,230,.18)' : 'rgba(14,20,48,.12)', overflow: 'hidden' }}>
            <i className="mo-wipe" style={{ ['--d' as string]: '900ms', display: 'block', height: '100%', width: `${count.pct}%`, background: state === 'dimmed' ? '#B9C3E6' : '#0E1430', borderRadius: 7 }} />
          </span>
          <span className="ex-tnum" style={{ font: '800 28px/1 var(--font-ui)', width: 60, textAlign: 'right' }}>{count.value}</span>
        </span>
      )}
    </>
  );
  const sizeStyle: CSSProperties = tv ? { padding: '20px 26px', gap: 22, flexWrap: 'wrap', cursor: 'default' } : {};
  return interactive
    ? <button type="button" className={`ex-option${className ? ' ' + className : ''}`} data-state={state} disabled={disabled} aria-pressed={['selected', 'saving', 'saved', 'locked-mine'].includes(state)} onClick={onSelect} style={{ ...sizeStyle, ...style }}>{body}</button>
    : <div className={`ex-option${className ? ' ' + className : ''}`} data-state={state} style={{ ...sizeStyle, ...style }}>{body}</div>;
}

/* ---------------- Save indicator ---------------- */
const SAVE: Record<SaveState, [string, IconName, string]> = {
  idle: ['No answer yet', 'dash', 'idle'], saving: ['Saving…', 'dot', 'saving'], saved: ['Saved', 'check', 'saved'],
  unsaved: ['Not saved · reconnecting', 'wifiOff', 'unsaved'], late: ['Not saved', 'x', 'unsaved'], locked: ['Locked', 'lock', 'locked'],
};
/** Fixed-height status pill. "Saved" only after server acknowledgement. */
export function SaveIndicator({ state, idleText, style }: { state: SaveState; idleText?: string; style?: CSSProperties }) {
  const [text, icon, data] = SAVE[state];
  return (
    <span className="ex-save" data-state={data} role="status" aria-live="polite" style={style}>
      <Icon name={icon} />{state === 'idle' && idleText ? idleText : text}
    </span>
  );
}

/* ---------------- Country answer input ---------------- */
/** Typed country answer. No suggestions or correctness hints before reveal. */
export function CountryInput({ value, onChange, locked, id = 'country' }: { value: string; onChange?: (v: string) => void; locked?: boolean; id?: string }) {
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.currentTarget.blur(); };
  return (
    <div>
      <label className="ex-label" htmlFor={id}>Country name</label>
      <input className="ex-field" id={id} value={value} readOnly={locked} data-state={locked ? 'locked' : 'default'}
        onChange={(e) => onChange?.(e.target.value)} onKeyDown={onKey}
        autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false} enterKeyHint="done" maxLength={40}
        placeholder="Type a country" aria-describedby={`${id}-help`} />
      <p className="ex-help" id={`${id}-help`}>{locked ? 'Locked. This is your accepted answer.' : 'Capitals, accents and small typos don’t matter.'}</p>
    </div>
  );
}
